import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMobileMode } from "./MobileModeContext.jsx";
import PhoneShell from "./shell/PhoneShell.jsx";
import PhoneHome from "./screens/PhoneHome.jsx";
import ScanScreen from "./screens/ScanScreen.jsx";
import QueueScreen from "./screens/QueueScreen.jsx";
import JobsScreen from "./screens/JobsScreen.jsx";
import JobDetailScreen from "./screens/JobDetailScreen.jsx";
import ReceivingScreen from "./screens/ReceivingScreen.jsx";
import CamerasScreen from "./screens/CamerasScreen.jsx";
import ConsumablesScreen from "./screens/ConsumablesScreen.jsx";
import BeaconsScreen from "./screens/BeaconsScreen.jsx";
import DashboardScreen from "./screens/DashboardScreen.jsx";
import ProgressScreen from "./screens/ProgressScreen.jsx";
import SalesOrdersScreen from "./screens/SalesOrdersScreen.jsx";
import SchedulingScreen from "./screens/SchedulingScreen.jsx";
import PurchaseOrdersScreen from "./screens/PurchaseOrdersScreen.jsx";
import InvoicesScreen from "./screens/InvoicesScreen.jsx";
import CustomersScreen from "./screens/CustomersScreen.jsx";
import VendorsScreen from "./screens/VendorsScreen.jsx";
import UsersScreen from "./screens/UsersScreen.jsx";
import FinanceScreen from "./screens/FinanceScreen.jsx";
import PartsScreen from "./screens/PartsScreen.jsx";
import MaterialsScreen from "./screens/MaterialsScreen.jsx";
import SensorsScreen from "./screens/SensorsScreen.jsx";
import ShippingScreen from "./screens/ShippingScreen.jsx";

// How App.jsx's routes pick the phone layout (rules: ./mobileMode.js). A page switched on for
// phones renders inside the phone shell; every other page keeps today's chrome and screen.

/** Around the app's routes: the phone shell, or today's chrome (`desktopChrome(children)`). */
export function PhoneAwareChrome({ desktopChrome, children }) {
  const location = useLocation();
  const { phoneLayoutFor } = useMobileMode();
  if (phoneLayoutFor(location.pathname)) return <PhoneShell>{children}</PhoneShell>;
  return desktopChrome(children);
}

/** "/": the phone home when the phone app is on, else `fallback` (today's menu / home page). */
export function PhoneHomeRoute({ fallback }) {
  const { phoneLayoutFor } = useMobileMode();
  return phoneLayoutFor("/") ? <PhoneHome /> : fallback;
}

// A route with a phone screen: that screen when its page is on for phones, else `fallback` (the
// desktop page; on a phone that is the "Use desktop layout" escape). On a phone's first visit it
// waits for GET /features, so a scanned link isn't used up by the desktop page before the switch
// answers (a desktop-width screen is always ready).
function PhoneOr({ path, screen, fallback }) {
  const { phoneLayoutFor, ready } = useMobileMode();
  if (phoneLayoutFor(path)) return screen;
  if (!ready) {
    return (
      <div className="text-center p-4" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      </div>
    );
  }
  return fallback;
}

/** "/queue": the phone Job Queue, else the desktop Job Queue. (The old separate phone page,
 *  MobileJobQueuePage, was deleted when the phone queue went on in prod, 2026-10-09.) */
export function PhoneQueueRoute({ fallback }) {
  return <PhoneOr path="/queue" screen={<QueueScreen />} fallback={fallback} />;
}

/** "/jobs": the phone Jobs list, else the desktop Jobs page. */
export function PhoneJobsRoute({ fallback }) {
  return <PhoneOr path="/jobs" screen={<JobsScreen />} fallback={fallback} />;
}

/** "/receiving": the phone Receiving page, else the desktop one. */
export function PhoneReceivingRoute({ fallback }) {
  return <PhoneOr path="/receiving" screen={<ReceivingScreen />} fallback={fallback} />;
}

/** "/cameras" (inside the admin route): the phone Cameras page, else the desktop Cameras page (a
 *  phone's "Use desktop layout" escape). The old separate phone page, MobileCamerasPage, was
 *  deleted when this one went on in prod (2026-10-13). */
export function PhoneCamerasRoute({ fallback }) {
  return <PhoneOr path="/cameras" screen={<CamerasScreen />} fallback={fallback} />;
}

/** "/consumables" (inside the admin route): the phone Consumables page (an item when the address
 *  carries ?id=N, the bin label's QR code), else the desktop page. */
export function PhoneConsumablesRoute({ fallback }) {
  return <PhoneOr path="/consumables" screen={<ConsumablesScreen />} fallback={fallback} />;
}

/** "/beacons" (inside the admin route): the phone Beacons list (?beacon=BCN-003 picks one out),
 *  else the desktop page. Both say so when pallet beacons are switched off. */
export function PhoneBeaconsRoute({ fallback }) {
  return <PhoneOr path="/beacons" screen={<BeaconsScreen />} fallback={fallback} />;
}

/** "/dashboard": the phone Dashboard (the alert cards), else the desktop Dashboard. */
export function PhoneDashboardRoute({ fallback }) {
  return <PhoneOr path="/dashboard" screen={<DashboardScreen />} fallback={fallback} />;
}

/** "/progress": the phone Progress page, else the desktop one. */
export function PhoneProgressRoute({ fallback }) {
  return <PhoneOr path="/progress" screen={<ProgressScreen />} fallback={fallback} />;
}

/** "/sales-orders" (and "?id=N"): the phone Sales Orders page, else the desktop Sales Order page. */
export function PhoneSalesOrdersRoute({ fallback }) {
  return <PhoneOr path="/sales-orders" screen={<SalesOrdersScreen />} fallback={fallback} />;
}

/** "/purchase-orders" (and "?id=N", "?po=<PO #>"): the phone Purchase Orders page (read-only), else
 *  the desktop page. */
export function PhonePurchaseOrdersRoute({ fallback }) {
  return <PhoneOr path="/purchase-orders" screen={<PurchaseOrdersScreen />} fallback={fallback} />;
}

/** "/invoices" (and "?id=N"): the phone Invoices page (read-only), else the desktop Invoices page. */
export function PhoneInvoicesRoute({ fallback }) {
  return <PhoneOr path="/invoices" screen={<InvoicesScreen />} fallback={fallback} />;
}

/** "/customers", "?id=N" and the desktop's "/customers/:customerId": the phone Customers page, else the
 *  desktop page. */
export function PhoneCustomersRoute({ fallback }) {
  const location = useLocation();
  return <PhoneOr path={location.pathname} screen={<CustomersScreen />} fallback={fallback} />;
}

/** "/vendors" (and "?id=N"): the phone Vendors page, else the desktop page. */
export function PhoneVendorsRoute({ fallback }) {
  return <PhoneOr path="/vendors" screen={<VendorsScreen />} fallback={fallback} />;
}

/** "/user-management" (and "?id=N"): the phone User Management page, else the desktop page. */
export function PhoneUsersRoute({ fallback }) {
  return <PhoneOr path="/user-management" screen={<UsersScreen />} fallback={fallback} />;
}

/** "/finance": the phone Finance page, else the desktop Finance page. */
export function PhoneFinanceRoute({ fallback }) {
  return <PhoneOr path="/finance" screen={<FinanceScreen />} fallback={fallback} />;
}

/** "/parts", "?id=N" and the desktop's "/parts/:partId": the phone Parts page, else the desktop page. */
export function PhonePartsRoute({ fallback }) {
  const location = useLocation();
  return <PhoneOr path={location.pathname} screen={<PartsScreen />} fallback={fallback} />;
}

/** "/materials-entry" (?id=N one material, ?tab=tracking the lot lookup, ?mtn=N one lot): the phone Materials
 *  page, else the desktop Materials page. */
export function PhoneMaterialsRoute({ fallback }) {
  return <PhoneOr path="/materials-entry" screen={<MaterialsScreen />} fallback={fallback} />;
}

/** "/sensors" (where a shop sensor's alert push lands): the phone Shop sensors page, else the desktop page. */
export function PhoneSensorsRoute({ fallback }) {
  return <PhoneOr path="/sensors" screen={<SensorsScreen />} fallback={fallback} />;
}

/** "/shipping-queue" (?tab=&so=&pallet=): the phone Shipping hub, else the desktop Shipping page. */
export function PhoneShippingRoute({ fallback }) {
  return <PhoneOr path="/shipping-queue" screen={<ShippingScreen />} fallback={fallback} />;
}

/** "/scheduling": the phone Scheduling page, else the desktop Scheduling page. */
export function PhoneSchedulingRoute({ fallback }) {
  return <PhoneOr path="/scheduling" screen={<SchedulingScreen />} fallback={fallback} />;
}

/** "/jobs/:jobId": the phone job detail, else the desktop job detail page. */
export function PhoneJobDetailRoute({ fallback }) {
  const location = useLocation();
  return <PhoneOr path={location.pathname} screen={<JobDetailScreen />} fallback={fallback} />;
}

/** "/scan" (the Scan tab and the home-screen "Scan" shortcut): a phone page only. */
export function PhoneScanRoute() {
  const { phoneLayoutFor, ready } = useMobileMode();
  if (phoneLayoutFor("/scan")) return <ScanScreen />;
  // First visit on this device: wait for GET /features before sending a "Scan" shortcut home.
  if (!ready) {
    return (
      <div className="text-center p-4" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      </div>
    );
  }
  return <Navigate to="/" replace />;
}
